import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash, X, Image, Check, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000') + '/api';

const AdminCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewSlide, setPreviewSlide] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    cta_text: '',
    cta_link: '/shop',
    image_url: '',
    order: 0,
    is_active: true
  });

  const fetchSlides = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/carousel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setSlides(data.data);
      }
    } catch (error) {
      console.error('Error fetching slides:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      cta_text: '',
      cta_link: '/shop',
      image_url: '',
      order: 0,
      is_active: true
    });
    setEditingSlide(null);
  };

  const handleOpenModal = (slide = null) => {
    if (slide) {
      setEditingSlide(slide.id);
      setFormData({
        title: slide.title,
        subtitle: slide.subtitle || '',
        description: slide.description || '',
        cta_text: slide.cta_text || '',
        cta_link: slide.cta_link || '/shop',
        image_url: slide.image_url,
        order: slide.order || 0,
        is_active: slide.is_active
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const url = editingSlide 
        ? `${API_BASE}/admin/carousel/${editingSlide}`
        : `${API_BASE}/admin/carousel`;
      
      const res = await fetch(url, {
        method: editingSlide ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (data.success) {
        toast.success(editingSlide ? 'Slide updated successfully' : 'Slide created successfully');
        setShowModal(false);
        resetForm();
        fetchSlides();
      } else {
        toast.error(data.detail || 'Failed to save slide');
      }
    } catch (error) {
      console.error('Error saving slide:', error);
      toast.error('Failed to save slide');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/carousel/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (data.success) {
        toast.success('Slide deleted successfully');
        fetchSlides();
      } else {
        toast.error(data.detail || 'Failed to delete slide');
      }
    } catch (error) {
      console.error('Error deleting slide:', error);
      toast.error('Failed to delete slide');
    }
  };

  const toggleActive = async (slide) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/carousel/${slide.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !slide.is_active })
      });

      const data = await res.json();

      if (data.success) {
        toast.success(`Slide ${slide.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchSlides();
      }
    } catch (error) {
      console.error('Error toggling slide:', error);
    }
  };

  const nextPreview = () => {
    if (slides.length > 0) {
      setPreviewSlide((prev) => (prev + 1) % slides.length);
    }
  };

  const prevPreview = () => {
    if (slides.length > 0) {
      setPreviewSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl text-secondary">Hero Carousel</h1>
          <p className="font-body text-sm text-text-muted">Manage hero section slides</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} /> Add Slide
        </button>
      </div>

      {/* Preview Section */}
      {slides.length > 0 && (
        <div className="admin-card">
          <h3 className="font-display text-lg text-secondary mb-4">Live Preview</h3>
          <div className="relative h-64 md:h-80 rounded-lg overflow-hidden">
            {slides.length > 0 && slides[previewSlide] ? (
              <>
                <img
                  src={slides[previewSlide].image_url}
                  alt={slides[previewSlide].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
                <div className="absolute inset-0 flex items-center">
                  <div className="p-8 max-w-xl">
                    <h4 className="font-display text-3xl text-white mb-2">
                      {slides[previewSlide].title}
                    </h4>
                    {slides[previewSlide].subtitle && (
                      <p className="font-display text-xl text-primary italic mb-4">
                        {slides[previewSlide].subtitle}
                      </p>
                    )}
                    {slides[previewSlide].description && (
                      <p className="font-body text-gray-300 mb-4 line-clamp-2">
                        {slides[previewSlide].description}
                      </p>
                    )}
                  </div>
                </div>
                {/* Preview Navigation */}
                {slides.length > 1 && (
                  <>
                    <button
                      onClick={prevPreview}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30"
                    >
                      <CaretLeft size={20} />
                    </button>
                    <button
                      onClick={nextPreview}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30"
                    >
                      <CaretRight size={20} />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {slides.map((_, idx) => (
                        <div
                          key={idx}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === previewSlide ? 'bg-primary w-6' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <p className="text-gray-400">No active slides</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slides List */}
      <div className="admin-card">
        <h3 className="font-display text-lg text-secondary mb-4">All Slides ({slides.length})</h3>
        
        {slides.length === 0 ? (
          <div className="text-center py-12">
            <Image size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No slides added yet</p>
            <button
              onClick={() => handleOpenModal()}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus size={18} /> Add First Slide
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {slides.map((slide) => (
              <div
                key={slide.id}
                className="flex items-center gap-4 p-4 bg-background-subtle rounded-lg"
              >
                <img
                  src={slide.image_url}
                  alt={slide.title}
                  className="w-24 h-16 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-display text-secondary">{slide.title}</h4>
                  <p className="font-body text-sm text-text-muted truncate">
                    {slide.subtitle || slide.description || 'No description'}
                  </p>
                  <p className="font-body text-xs text-text-muted">
                    Order: {slide.order} | {slide.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(slide)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      slide.is_active
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    <Check size={18} />
                  </button>
                  <button
                    onClick={() => handleOpenModal(slide)}
                    className="w-10 h-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center hover:bg-primary/20"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(slide.id)}
                    className="w-10 h-10 bg-red-50 text-red-500 rounded-lg flex items-center justify-center hover:bg-red-100"
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="modal-content max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-display text-xl text-secondary">
                  {editingSlide ? 'Edit Slide' : 'Add New Slide'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-10 h-10 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Image URL */}
                <div>
                  <label className="form-label">Image URL *</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="form-input"
                    placeholder="https://example.com/image.jpg"
                    required
                  />
                  {formData.image_url && (
                    <div className="mt-2 h-32 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="form-input"
                    placeholder="Elegance"
                    required
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="form-label">Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="form-input"
                    placeholder="Redefined"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="form-label">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="form-input"
                    rows={3}
                    placeholder="Discover our exquisite collection..."
                  />
                </div>

                {/* CTA Text & Link */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Button Text</label>
                    <input
                      type="text"
                      value={formData.cta_text}
                      onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                      className="form-input"
                      placeholder="Shop Now"
                    />
                  </div>
                  <div>
                    <label className="form-label">Button Link</label>
                    <input
                      type="text"
                      value={formData.cta_link}
                      onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                      className="form-input"
                      placeholder="/shop"
                    />
                  </div>
                </div>

                {/* Order & Active */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-8">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 text-primary rounded"
                    />
                    <label htmlFor="is_active" className="font-body text-sm text-secondary">
                      Active (show on website)
                    </label>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary"
                  >
                    {submitting ? 'Saving...' : editingSlide ? 'Update Slide' : 'Create Slide'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCarousel;
